import config from 'config';
import TessituraAPI from 'lib/api/tessitura-wrapper';
import fallbacks from 'lib/common/fallbacks';
import features from 'lib/common/features';
import logger from 'lib/common/logger';
import { enhanceSeatData, setScreenZoneData } from 'lib/common/transforms';

import get from 'lodash/get';
import has from 'lodash/has';
import isEmpty from 'lodash/isEmpty';
import isObject from 'lodash/isObject';

var API;
var INITIAL_SESSION_KEY;

export function configure(endpoint, initialSessionKey) {
  var apiInstance = new TessituraAPI(endpoint, initialSessionKey);
  API = apiInstance;

  if (initialSessionKey) {
    setInitialSessionKey(initialSessionKey);
  }
}

export function getInitialSessionKey() {
  return INITIAL_SESSION_KEY || '%sessionKey%';
}

export function setInitialSessionKey(sessionKey) {
  INITIAL_SESSION_KEY = sessionKey;
  return INITIAL_SESSION_KEY;
}

export function getSessionKey() {
  let initialSessionKey = getInitialSessionKey();
  if (features.isEnabled('cookieGetCurrentSessionKey')) {
    const cookieValue = document.cookie
      .split('; ')
      .find(row =>
        row.startsWith(
          features.getOption('cookieGetCurrentSessionKey', 'cookie')
        )
      );

    if (cookieValue) {
      const session_key = cookieValue.split('=')[1];
      return Promise.resolve(session_key);
    }
  }

  if (features.isEnabled('customGetCurrentSessionKey')) {
    return API.requestRaw({
      method: 'getCurrentSessionKey',
      params: {}
    }).then(response => {
      const sessionKey = get(response, 'result');
      setInitialSessionKey(sessionKey);
      return sessionKey;
    });
  } else if (initialSessionKey) {
    return Promise.resolve(initialSessionKey);
  } else {
    return getSessionKey();
  }
}

export function loginInfo() {
  return API.request({
    method: 'LoginInfoEx',
    params: { sessionKey: getInitialSessionKey() }
  }).then(response => {
    if (has(response, 'result.LoginInfoResults.LoginInformation')) {
      return response.result.LoginInfoResults.LoginInformation;
    } else {
      logger.captureMessage('Failed to load LoginInfoEx');
      return false;
    }
  });
}

// @TODO: Decouple this method
export function getSeatmapData(
  performanceId,
  packageNo,
  currentMOS,
  sourceCode,
  usesGeneratedSeatmap,
  refreshSeatData
) {
  let promises = [];

  /**
     * By default GetSeatsBriefEx requires a session key, meaning each user
     * gets a unique cache hit, so the cache only works for subsequent calls.
     *
     * If cacheableGetSeats is enabled, assuming a GetSeatsBriefExMOS method,
     * we swap out the get seats call out with one that doesn't require
     * a session key; in place we need to pass MOS and source code.
     *
     * This allows us to cache the call in a non-unique way,
     * which greatly increases the value of the cache.
     */
  if (features.isEnabled('cacheableGetSeats')) {
    let params = {
      iPerformanceNumber: packageNo ? -1 : performanceId,
      iPackageNumber: packageNo ? packageNo : 0,
      cSummaryOnly: 'N',
      cCalcPackageAlloc: 'N',
      cReturnNonSeats: usesGeneratedSeatmap ? 'Y' : 'N',
      iModeOfSale: currentMOS,
      sourceCode: sourceCode
    };

    if (refreshSeatData) {
      params.xRefresh = true;
    }

    promises = promises.concat([
      API.requestGet({
        method: 'GetSeatsBriefWithMOS',
        params: params
      })
    ]);
  } else {
    promises = promises.concat([
      API.requestGet({
        method: 'GetSeatsBriefEx',
        params: {
          sSessionKey: getInitialSessionKey(),
          iPerformanceNumber: packageNo ? -1 : performanceId,
          iPackageNumber: packageNo ? packageNo : 0,
          cSummaryOnly: 'N',
          cCalcPackageAlloc: 'N',
          cReturnNonSeats: usesGeneratedSeatmap ? 'Y' : 'N'
        }
      })
    ]);
  }

  promises = promises.concat([
    API.requestGet({
      method: 'GetSeatStatus',
      params: {}
    })
  ]);

  if (usesGeneratedSeatmap && !packageNo) {
    promises = promises.concat([
      API.requestGet({
        method: 'GetScreens',
        params: {
          sSessionKey: getInitialSessionKey(),
          iPerformanceNumber: performanceId
        }
      }),
      API.requestGet({
        method: 'GetSeats',
        params: {
          sSessionKey: getInitialSessionKey(),
          iPerformanceNumber: performanceId,
          cSummaryOnly: 'Y',
          cCalcPackageAlloc: 'Y',
          cReturnNonSeats: 'Y'
        }
      })
    ]);
  }

  return Promise.all(promises)
    .then(function(responses) {
      var seatsBriefResults = get(
        responses[0],
        'result.GetSeatsBriefExResults'
      );
      var seatStatusResults = get(responses[1], 'result.GetSeatStatusResults');
      var screensResults = get(responses[2], 'result.GetScreensResults');
      var seatsWithZones = get(responses[3], 'result.GetSeatsResults');

      var seatsResults = enhanceSeatData(
        get(seatsBriefResults, 'S'),
        get(seatStatusResults, 'Status'),
        get(seatsBriefResults, 'Section'),
        get(screensResults, 'Screens')
      );

      var screenZoneData = seatsWithZones && setScreenZoneData(seatsWithZones);

      return {
        screensResults: screensResults,
        seatsStatusResults: seatStatusResults,
        seatsResults: seatsResults,
        screenZoneData: screenZoneData
      };
    })
    .catch(fallbacks.redirectWithError);
}

export function getMaxTicketAmount() {
  if (config.customMaxTicketsLimit) {
    return Promise.resolve(config.customMaxTicketsLimit);
  } else {
    return API.requestGet({
      method: 'GetMaxTicketAmount',
      params: {}
    }).then(function(response) {
      let result = response.result;
      if (isObject(result)) {
        return parseInt(result[0], 10);
      } else {
        return result;
      }
    });
  }
}

export function getDetailsWithDiscounting(perfNo, packageNo, loginInformation) {
  let promise;
  if (packageNo) {
    promise = API.requestGet({
      method: 'GetPackageDetailWithDiscountingEx',
      params: {
        SessionKey: getInitialSessionKey(),
        PackageID: packageNo,
        ModeOfSale: loginInformation.MOS
      }
    }).then(response =>
      get(response, 'result.GetPackageDetailWithDiscountingExResult')
    );
  } else {
    promise = API.requestGet({
      method: 'GetPerformanceDetailWithDiscountingEx',
      params: {
        SessionKey: '',
        iPerf_no: perfNo,
        iModeOfSale: loginInformation.MOS,
        sContentType: ''
      }
    }).then(response =>
      get(response, 'result.GetPerformanceDetailWithDiscountingExResult')
    );
  }

  return promise.catch(fallbacks.redirectWithError);
}

export function getProductionDetail(performanceId, modeOfSale) {
  return API.requestGet({
    method: 'GetProductionDetailEx3',
    params: {
      SessionKey: getInitialSessionKey(),
      iPerf_no: performanceId,
      iModeOfSale: modeOfSale,
      sContentTypes: '[]',
      sPerformanceContentTypes: '[]'
    }
  }).then(response => get(response, 'result.GetProductionDetailEx3Result'));
}

export function getPackageSelector(packageId, modeOfSale) {
  return API.requestGet({
    method: 'GetPackageSelector',
    params: {
      SessionKey: getInitialSessionKey(),
      packageId: packageId,
      iModeOfSale: modeOfSale
    }
  }).then(response => get(response, 'result'));
}

export function getCart() {
  return API.request({
    method: 'GetCart',
    params: { sWebSessionID: getInitialSessionKey() }
  }).then(function(response) {
    if (response) {
      let result = response.result.GetCartResults;
      return !isEmpty(result) ? result : false;
    } else {
      return false;
    }
  });
}

export function ReserveTicketsSpecifiedSeats(
  performanceId,
  lineItems,
  leaveSingleSeats
) {
  let numbersOfSeats = lineItems.length,
    priceType = lineItems.map(item => item.priceData.price_type).join(','),
    requestedSeats = lineItems.map(item => item.seatData.seat_no).join(','),
    podSeats = get(lineItems, 'podSeats');

  if (podSeats !== undefined && podSeats.length) {
    priceType = '';
    requestedSeats = '';
    numbersOfSeats = 0;

    delete lineItems['podSeats'];
    podSeats.forEach(pod => {
      lineItems.forEach(lineItem => {
        if (lineItem.seatData.section == pod.section) {
          const seats = pod.seats.slice(0, pod.amount);
          priceType += `${seats.map(seat => pod.priceType).join(',')},`;
          numbersOfSeats += seats.length;
          requestedSeats += `${seats.join(',')},`;
        }
      });
    });

    priceType = priceType.slice(0, -1);
    requestedSeats = requestedSeats.slice(0, -1);
  }

  if (features.isEnabled('podSeats')) {
    leaveSingleSeats = 'Y';
  }

  return API.request({
    method: 'ReserveTicketsSpecifiedSeats',
    params: {
      sWebSessionID: getInitialSessionKey(),
      // TODO: map these before passing through
      sPriceType: priceType,
      iPerformanceNumber: performanceId,
      iNumberOfSeats: numbersOfSeats,
      sSpecialRequests: `LeaveSingleSeats=${leaveSingleSeats}`,
      RequestedSeats: requestedSeats,
      iZone: 0
    }
  });
}

export function ReleaseTickets(iPerformanceNumber, iLineItemNumber) {
  return API.request({
    method: 'ReleaseTickets',
    params: {
      sWebSessionID: getInitialSessionKey(),
      iPerformanceNumber: iPerformanceNumber,
      iLineItemNumber: iLineItemNumber
    }
  });
}

export function RemoveNFSPackage(performanceLineItemId, performanceNumber) {
  return API.request({
    method: 'RemoveNFSPackage',
    params: {
      SessionKey: getInitialSessionKey(),
      NFSPackageLineItemId: 0,
      PerformanceLineItemId: performanceLineItemId,
      PerformanceNumber: performanceNumber
    }
  });
}

export function AddNFSPackagePerformanceItem(
  NFSPackageLineItemId,
  PackageNumber,
  PerformanceGroupNumber,
  lineItems,
  perfNo
) {
  var priceTypes = lineItems.map(item => item.priceData.price_type).join(',');
  var requestedSeats = lineItems.map(item => item.seatData.seat_no).join(',');

  return API.request({
    method: 'AddNFSPackagePerformanceItem',
    params: {
      SessionKey: getInitialSessionKey(),
      NFSPackageLineItemId: NFSPackageLineItemId || 0,
      PriceType: priceTypes,
      PackageNumber: PackageNumber,
      PerformanceGroupNumber: PerformanceGroupNumber,
      PerformanceNumber: perfNo,
      NumberOfSeats: lineItems.length,
      Zone: 0,
      RequestedSeats: requestedSeats,
      LeaveSingleSeats: 'true',
      SpecialRequests: '',
      UnSeated: 'false'
    }
  });
}

export function AddPackageItemSeated(
  currentFixedPackage,
  lineItems,
  leaveSingleSeats
) {
  return API.request({
    method: 'AddPackageItemSeated',
    params: {
      SessionKey: getInitialSessionKey(),
      PriceType: lineItems.map(item => item.priceData.price_type).join(','),
      PackageNumber: currentFixedPackage.pkg_no,
      NumberOfSeats: lineItems.length,
      Zone: currentFixedPackage.zone_no,
      RequestedSeats: lineItems.map(item => item.seatData.seat_no).join(','),
      LeaveSingleSeats: leaveSingleSeats === 'Y' ? 'true' : 'false'
    }
  });
}

export function RemovePackageItem(pkgNo, liSeqNo) {
  return API.request({
    method: 'RemovePackageItem',
    params: {
      SessionKey: getInitialSessionKey(),
      PackageNumber: pkgNo,
      PackageLineItemID: liSeqNo
    }
  });
}

export function GetPromoCodeEx(sourceCode) {
  return API.requestGet({
    method: 'GetPromoCodeEx',
    params: {
      SessionKey: getInitialSessionKey(),
      PromotionCodeString: '',
      PromotionCode: sourceCode
    }
  }).then(function(promoCodeResponse) {
    var details = get(
      promoCodeResponse,
      'result.GetPromoCodeExResults.PromoCodeDetails'
    );
    return details;
  });
}

export function SeatUpsells() {
  return API.requestGet({
    method: 'SeatUpsells',
    params: {}
  }).then(function(response) {
    var result = get(response, 'result.GetLocalDataResults.LocalData');
    return result;
  });
}

export function StoreADACSIData(data) {
  return API.request({
    method: 'StoreADACSIData',
    params: {
      SessionKey: getInitialSessionKey(),
      csiData: data
    }
  });
}

export default {
  configure,
  getInitialSessionKey,
  setInitialSessionKey,
  getSessionKey,
  loginInfo,
  getSeatmapData,
  getMaxTicketAmount,
  getDetailsWithDiscounting,
  getProductionDetail,
  getPackageSelector,
  getCart,
  ReserveTicketsSpecifiedSeats,
  ReleaseTickets,
  RemoveNFSPackage,
  AddNFSPackagePerformanceItem,
  AddPackageItemSeated,
  RemovePackageItem,
  GetPromoCodeEx,
  SeatUpsells,
  StoreADACSIData
};



// WEBPACK FOOTER //
// ./src/javascripts/app/services/API.js